// Story ▸ Continuity (StoryViews.swift's `ContinuityView`): the manuscript checked against its story times, settings,
// birth dates, travel and beat sheet — two places at once, journeys too quick for the story's ways of travelling, ages,
// weekdays, the moon, daylight, structure. Each finding is a
// question to answer, with Open Scene and Add to Margin (a "Continuity check" comment on the passage, added once however
// often the checks run); Add All to Margin; Check Again after changes.
import { useMemo, useState } from 'react';
import { CONTINUITY_LABELS, type ContinuityFinding } from '@writing-suite/core/story';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { announce } from '../../shell/context.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { addContinuityToMargin, continuityFindings, continuityInMargin, findingPassage } from '../../store/story.ts';
import { plural } from './common.tsx';

export function ContinuityPage({ project, live }: PageProps) {
  const ws = useWorkspace();
  // Checked when the page opens and on Check Again (not at every rebuild: the checks read the whole book).
  const [run, setRun] = useState(0);
  const findings = useMemo(() => continuityFindings(project), [run]); // eslint-disable-line react-hooks/exhaustive-deps
  const [added, setAdded] = useState<string | null>(null);
  const inMargin = continuityInMargin(project, findings);
  const canWrite = !!ws?.canWrite && !!live;
  const add = (list: ContinuityFinding[]) => {
    if (!live) return 0;
    const n = addContinuityToMargin(live, list);
    const text = n === 0 ? 'Already in the margin.' : `${plural(n, 'finding')} added to the margin.`;
    setAdded(text);
    announce(text);
    return n;
  };
  return (
    <div className="story-page continuity-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Continuity</h1>
      </header>
      <div className="continuity-intro">
        <p className="lede">
          People in two places at once or without time to travel between them, ages against birth dates, weekdays against dates, the sun and moon against the date and place, and, with a beat sheet, beats without
          scenes, scenes without beats and beats out of place.
        </p>
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => { setAdded(null); setRun(run + 1); }}>
            Check Again
          </button>
          {findings.length && canWrite ? (
            <button type="button" className="button primary" onClick={() => add(findings)}>
              Add All to Margin
            </button>
          ) : null}
        </div>
        {added ? <p className="status-line" role="status">{added}</p> : null}
      </div>
      {findings.length === 0 ? (
        <div className="story-empty" role="note" data-findings="0">
          <p className="story-empty-title">Nothing to check</p>
          <p>No conflicts found in scenes with story data. Scenes without a story time or setting aren’t checked for time or sky.</p>
        </div>
      ) : (
        <ul className="continuity-list" data-findings={findings.length}>
          {findings.map((f) => (
            <li key={f.id} className="continuity-finding" data-finding-id={f.id} data-kind={f.kind}>
              <p className="finding-head">
                <span className="finding-kind">{CONTINUITY_LABELS[f.kind]}</span>
                <span className="finding-scene">{f.fragmentTitle}</span>
              </p>
              <p className="finding-message">{f.message}</p>
              <p className="finding-actions">
                <button type="button" className="link-button" onClick={() => ws?.reveal(findingPassage(project, f))}>
                  Open Scene
                </button>
                {inMargin.has(f.id) ? (
                  <span className="finding-added">In the margin</span>
                ) : canWrite ? (
                  <button type="button" className="link-button" onClick={() => add([f])}>
                    Add to Margin
                  </button>
                ) : null}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
